import { Col, Row } from 'antd';
import classNames from 'classnames';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import AddressBookControls from '../dialogs/shipments/AddressBookControls';
import {
  AddressLookupCard,
  NewShipmentBaseRow,
  NewShipmentForm,
} from '../forms/shipments/newShipment/newShipmentElements';
import {
  DeliveryDateTimeField,
  PickupDateTimeField,
} from '../forms/shipments/shipmentFormsCommon/shipmentDateFields';
import { useNewQuoteFormStyles } from '../forms/shipments/quotes/newQuoteForm.styles';
import { ServiceTypeField } from '../forms/shipments/shipmentFormsCommon/shipmentServiceInformationFields';
import { FormItemSchemaDefinedSelect } from '../components/forms/selects';
import { ConfigurableCard } from '../components/layout/cardElements';
import { useNewShipmentElementsStyles } from '../forms/shipments/newShipment/newShipmentElements.styles';
import { useNewShipmentServiceInformationFormStyles } from '../forms/shipments/newShipment/NewShipmentServiceInformationForm.styles';
import { pxToNumber } from '../utils/cssUtils';
import { cssVariables } from '../styles/cssVariables';
import {
  DangerousGoodsSection,
  IncotermsField,
  ReferenceInformationCard,
  ShipperSpecificCard,
} from '../forms/shipments/newShipment/NewShipmentServiceInformationForm';
import { PackageListSummary } from '../forms/shipments/packageList/PackageListSummary';
import PackageListPackages from '../forms/shipments/packageList/PackageListPackages';
import { useQuotePackageListStyles } from '../forms/shipments/packageList/QuotePackageList.styles';
import { useDynamicFormSchemaFieldExists } from '../components/forms/dynamic/dynamicFormSchema';

const NewSinglePageContent = ({
  activeTemplateCounter,
  onEditedPackageChange,
  refreshValues,
  ...props
}) => {
  const { styles } = useNewQuoteFormStyles();
  const { styles: quotePackageListStyles } = useQuotePackageListStyles();
  const { styles: newShipmentElementsStyles } = useNewShipmentElementsStyles();
  const { styles: serviceInformationStyles } =
    useNewShipmentServiceInformationFormStyles();
  const showIncoterms = useDynamicFormSchemaFieldExists({
    schemaName: 'serviceInformation.incoterms',
  });
  const topRowSpan = showIncoterms ? 8 : 12;

  const onValuesChange = useCallback(() => refreshValues(), [refreshValues]);

  return (
    <NewShipmentForm {...props} name="origin" onValuesChange={onValuesChange}>
      <Row className={classNames('flex-nowrap', styles.root)}>
        <Col className="Flex1 spaces-vert-norm2" lg={24}>
          <ConfigurableCard
            titleId="book.newShipment.serviceInformation"
            bodyComponent="div"
            bodyClassName={classNames(
              newShipmentElementsStyles.newShipmentCardBody,
              styles.newShipmentServiceInformationCard,
              serviceInformationStyles.newShipmentServiceInformationCard
            )}
          >
            <NewShipmentBaseRow>
              <Col xs={12}>
                <NewShipmentBaseRow>
                  <Col
                    xs={24}
                    md={12}
                    className={classNames(
                      styles.jobLocationCardSection,
                      newShipmentElementsStyles.jobLocationCardSection
                    )}
                  >
                    <div
                      className={classNames(
                        styles.jobLocationCardSectionHeader,
                        newShipmentElementsStyles.jobLocationCardSectionHeader
                      )}
                    >
                      <div
                        className={classNames(
                          'JobLocationCard-SectionTitle',
                          newShipmentElementsStyles.jobLocationCardSectionTitle,
                          'Flex1'
                        )}
                      >
                        <FormattedMessage id="book.newQuote.serviceRequested" />
                      </div>
                    </div>
                  </Col>
                </NewShipmentBaseRow>
                <NewShipmentBaseRow>
                  <Col xs={topRowSpan} className={styles.newQuoteCol}>
                    <ServiceTypeField namePrefix="serviceInformation" />
                  </Col>
                  <Col xs={topRowSpan} className={styles.newQuoteCol}>
                    <FormItemSchemaDefinedSelect
                      name={['serviceInformation', 'commodity']}
                      schemaName="serviceInformation.commodity"
                      labelId="book.newShipment.label.commodity"
                      allowFreeText
                      textAsValue
                    />
                  </Col>
                  {showIncoterms && (
                    <Col xs={topRowSpan}>
                      <IncotermsField namePrefix="serviceInformation" />
                    </Col>
                  )}
                </NewShipmentBaseRow>
              </Col>
              <Col xs={6}>
                <NewShipmentBaseRow>
                  <Col xs={24} className={styles.newQuoteCol}>
                    <PickupDateTimeField
                      getAddress={({ formValues }) =>
                        formValues.origin?.address
                      }
                      addressSchemaPrefix="origin"
                      namePrefix="origin"
                      validateHoliday
                    />
                  </Col>
                </NewShipmentBaseRow>
              </Col>
              <Col xs={6}>
                <DeliveryDateTimeField namePrefix="destination" />
              </Col>
            </NewShipmentBaseRow>
          </ConfigurableCard>
          <NewShipmentBaseRow
            gutter={[
              pxToNumber(cssVariables.spaceNorm2),
              pxToNumber(cssVariables.spaceNorm2),
            ]}
          >
            <Col xs={24} xl={12}>
              <AddressLookupCard
                titleId="book.newShipment.fromAddress"
                schemaNamePrefix="origin"
                enableAddressLookup
                fieldName="addressOrigin"
                namePrefix="origin"
                showCountry
                renderHeaderExtra={type => (
                  <AddressBookControls
                    name="origin"
                    type={type}
                    namePrefix="origin"
                  />
                )}
              />
            </Col>
            <Col xs={24} xl={12}>
              <AddressLookupCard
                titleId="book.newShipment.toAddress"
                schemaNamePrefix="destination"
                enableAddressLookup
                fieldName="addressDestination"
                namePrefix="destination"
                showCountry
                renderHeaderExtra={type => (
                  <AddressBookControls
                    name="destination"
                    type={type}
                    namePrefix="destination"
                  />
                )}
              />
            </Col>
          </NewShipmentBaseRow>
          <NewShipmentBaseRow>
            <Col lg={12} xs={24}>
              <ConfigurableCard
                titleId="book.newShipment.quantityWeightSummary"
                bodyComponent="div"
              >
                <Row
                  className={quotePackageListStyles.quotesFormPackageSummary}
                >
                  <PackageListSummary />
                </Row>
              </ConfigurableCard>
            </Col>
            <Col lg={12} xs={24}>
              <ConfigurableCard
                titleId="book.newShipment.dangerousGoodsDeclaration"
                bodyComponent="div"
                bodyClassName={classNames(
                  newShipmentElementsStyles.newShipmentCardBody,
                  styles.newShipmentServiceInformationCard,
                  serviceInformationStyles.newShipmentServiceInformationCard
                )}
              >
                <DangerousGoodsSection
                  noDivider
                  namePrefix="serviceInformation"
                />
              </ConfigurableCard>
            </Col>
          </NewShipmentBaseRow>
          <NewShipmentBaseRow>
            <Col xs={24} lg={12}>
              <PackageListPackages
                activeTemplateCounter={activeTemplateCounter}
                onEditedPackageChange={onEditedPackageChange}
              />
            </Col>
            <Col xs={24} lg={12} className="Flex1 spaces-vert-norm2">
              <NewShipmentBaseRow>
                <Col xs={24}>
                  <ShipperSpecificCard namePrefix="serviceInformation" />
                </Col>

                <Col xs={24}>
                  <ReferenceInformationCard namePrefix="serviceInformation" />
                </Col>
              </NewShipmentBaseRow>
            </Col>
          </NewShipmentBaseRow>
        </Col>
      </Row>
    </NewShipmentForm>
  );
};

export default NewSinglePageContent;
